import React from 'react';
import { Container, Accordion } from 'react-bootstrap';
import { motion } from 'framer-motion';
import faqData from '../data/faqData.json';
import SEO from '../components/SEO';
import '../styles/FAQ.css';

const FAQ = () => {
    const [searchQuery, setSearchQuery] = React.useState('');
    const [selectedCategory, setSelectedCategory] = React.useState('All');

    // Get unique categories
    const categories = ['All', ...new Set(faqData.map(item => item.category))];

    // Filter data
    const filteredData = faqData.filter(item => {
        const matchesSearch = item.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
            item.answer.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesCategory = selectedCategory === 'All' || item.category === selectedCategory;

        return matchesSearch && matchesCategory;
    });

    // But with Search, a flat list is usually better.
    // Let's stick to a flat list of Accordion items for the result, or maybe grouped if "All" is selected?
    // Let's try a clean flat list of accordion items to avoid complexity with the headers appearing/disappearing.
    // Wait, the prompt implies "show category as tags in top".

    const faqSchema = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqData.slice(0, 15).map(item => ({
            "@type": "Question",
            "name": item.question,
            "acceptedAnswer": {
                "@type": "Answer",
                "text": item.answer
            }
        }))
    };

    return (
        <Container fluid className="faq-section">
            <SEO
                title="Frequently Asked Questions (FAQ)"
                description="Find answers to common questions about Thalai Nagaram RP (TNRP): FiveM hardware requirements, Discord whitelisting, troubleshooting, and FiveM error code fixes."
                keywords="TNRP FAQ, Thalai Nagaram RP Questions, FiveM Hardware Requirements Tamil, FiveM Error Code Fixes, How to join TNRP, FiveM Low FPS Fix"
                canonicalUrl="https://thalainagaram.org/faq"
                schema={faqSchema}
            />
            <motion.div
                className="text-center mb-5"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
            >
                <h1 className="display-4 fw-bold mb-3" style={{ fontFamily: 'Orbitron, sans-serif', fontSize: '2.5rem' }}>
                    KNOWLEDGE <span className="text-neon-gold">BASE</span>
                </h1>
                <p className="lead" style={{ color: '#aaa', maxWidth: '600px', margin: '0 auto' }}>
                    Everything you need to know about the city regulations, requirements, and gameplay.
                </p>
            </motion.div>

            {/* Search Bar */}
            <motion.div
                className="search-container mb-4 mx-auto"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                style={{ maxWidth: '600px' }}
            >
                <input
                    type="text"
                    placeholder="Search for answers..."
                    className="faq-search-input"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                />
            </motion.div>

            {/* Category Tags */}
            <motion.div
                className="category-tags-container mb-5 d-flex flex-wrap justify-content-center gap-2"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
            >
                {categories.map((cat, index) => (
                    <button
                        key={index}
                        className={`faq-category-tag ${selectedCategory === cat ? 'active' : ''}`}
                        onClick={() => setSelectedCategory(cat)}
                    >
                        {cat}
                    </button>
                ))}
            </motion.div>

            <motion.div
                className="mx-auto"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4, duration: 0.8 }}
            >
                {filteredData.length > 0 ? (
                    <Accordion className="faq-accordion">
                        {filteredData.map((item, index) => (
                            <Accordion.Item eventKey={item.id.toString()} key={item.id} className="faq-item mb-3">
                                <Accordion.Header>
                                    <span className="me-3 fw-bold text-neon-gold">[{item.category}]</span>
                                    {item.question}
                                </Accordion.Header>
                                <Accordion.Body>
                                    <div dangerouslySetInnerHTML={{ __html: item.answer.replace('<Link>', '<a href="https://discord.gg/tnrp" target="_blank" rel="noreferrer" class="text-neon-gold">Discord</a>') }} />
                                </Accordion.Body>
                            </Accordion.Item>
                        ))}
                    </Accordion>
                ) : (
                    <div className="text-center mt-5">
                        <h4 className="text-white mb-3">No results found.</h4>
                        <p className="text-secondary">
                            Please contact tech support at our <a href="https://discord.gg/tnrp" target="_blank" rel="noreferrer" className="text-neon-gold text-decoration-none">Discord</a> server.
                        </p>
                    </div>
                )}
            </motion.div>
        </Container>
    );
};

export default FAQ;
